import dayjs from 'dayjs';
import { ROLES } from '../routes/Routes';
import _ from 'lodash';
import { env, images_cdn, netsuite_url } from '../components/config';
import { deliveryDays, VAT_RATE } from '../constants';

export const generateReduxKeys = (keys) => {
  const localStorageKeys = Object.keys(localStorage);

  const initialState = {};

  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  for (const key of Object.keys(keys)) {
    const generatedKey = key;
    const initialValue = keys[key];

    const keysList = localStorageKeys.filter((key) => key.endsWith(generatedKey));
    if (!keysList.includes(generatedKey)) initialState[generatedKey] = initialValue;

    keysList.forEach((key) => {
      const value = localStorage.getItem(key);

      if (value && isValidJSON(value)) {
        initialState[key] = JSON.parse(value);
      } else {
        initialState[key] = initialValue;
      }
    });
  }

  return initialState;
}

export const handleLogout = () => {
  localStorage.clear();
  window.location.href = '/';
}

export const getInitials = (name) => {
  if (!name) return '';

  const nameParts = name
    .split(' ')
    .map((part) => part.trim())
    .filter((part) => /^[A-Za-z]/.test(part));

  if (nameParts.length === 0) return '';
  if (nameParts.length === 1) return nameParts[0][0]?.toUpperCase() || '';

  const firstInitial = nameParts[0][0]?.toUpperCase() || '';
  const lastInitial = nameParts[nameParts.length - 1][0]?.toUpperCase() || '';

  return firstInitial + lastInitial;
};

export const getRoleByValue = (value) => {
  const key = Object.keys(ROLES).find((k) => ROLES[k] === value);
  return key ? key.replace(/_/g, ' ') : null;
};

export const formatCurrency = (amount, locale = 'en-GB', currency = 'GBP') => {
  let value;
  if (typeof amount === 'number') {
    value = amount;
  } else if (typeof amount === 'string') {
    value = Number(amount.replace(/[^\d.-]/g, ''));
  } else {
    value = 0;
  }

  value = isNaN(value) ? 0 : value;
  if (Math.abs(value) < 0.005) value = 0;
  const roundedValue = value.toFixed(2);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(roundedValue);
};

export const formatCount = (amount, locale = 'en-GB') => {
  let value =
    typeof amount === 'number'
      ? amount
      : Number(amount?.replace(/[^\d.-]/g, ''));

  value = isNaN(value) ? 0 : value;

  const roundedValue = Number(value.toFixed(2));

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(roundedValue);
};

export const getTimeSlots = (start = '09:00', end = '19:00', intervalMinutes = 30) => {
  const slots = [];
  let current = dayjs(start, 'HH:mm');
  const endTime = dayjs(end, 'HH:mm');

  while (current.isBefore(endTime) || current.isSame(endTime)) {
    const formatted24 = current.format('HH:mm'); // 09:00
    slots.push(formatted24);
    current = current.add(intervalMinutes, 'minute');
  }

  return slots;
};

export const formatAddress = (item) => {
  if (!item) return { fullAddress: '', formattedAddress: '' };

  const addressLines = Object.keys(item)
    .filter((key) => /^address_\d+$/.test(key) && item[key]?.trim())
    .map((key) => item[key].trim());

  const rawAddress =
    item.address?.trim() || item.fullAddress?.trim() || item.address_1?.trim() || '';

  const clean = (text) =>
    text
      .replace(/\s+/g, ' ')
      .replace(/,+/g, ',')
      .replace(/,\s*,+/g, ',')
      .replace(/,\s*$/, '')
      .trim();

  let fullAddress = '';
  let formattedAddress = '';

  if (addressLines.length) {
    fullAddress = clean([...addressLines, item.city, item.postcode].filter(Boolean).join(', '));
    formattedAddress = clean(
      [item.attention, item.addressee, ...addressLines, item.city, item.postcode]
        .filter(Boolean)
        .join(', ')
    );
  } else if (rawAddress) {
    fullAddress = clean(rawAddress);
    formattedAddress = clean(
      [item.attention, item.addressee, rawAddress].filter(Boolean).join(', ')
    );
  }

  return { fullAddress, formattedAddress };
};

export const calculateOrderSummary = (orders) => {
  const subtotal = _.sumBy(orders, (item) => (item.unit_price || 0) * (item.quantity || 0));

  const vat = _.sumBy(orders, (item) => {
    const isTaxable = parseInt(item.vat) === 20;
    return isTaxable ? (item.unit_price || 0) * (item.quantity || 0) * VAT_RATE : 0;
  });

  const grandTotal = subtotal + vat;

  return {
    subtotal,
    vat,
    grandTotal,
  };
};

export const getProductImageUrl = (product) => {
  if (!product?.profile_image) return null;
  return `${images_cdn}/${env}/warehouse-products/${product?.netsuite_id || product?.product_netsuite_id}/${product?.profile_image}`;
};

export const calculateVAT = ({ unitPrice, quantity, vatField }) => {
  const price = Number(unitPrice) || 0;
  const qty = Number(quantity) || 0;
  const isTaxable = parseInt(vatField) === 20;
  const vatRate = isTaxable ? VAT_RATE : 0;

  const subtotal = price * qty;
  const vatAmount = subtotal * vatRate;
  const totalWithVAT = subtotal + vatAmount;

  return { subtotal, vatAmount, totalWithVAT };
};

export const getAddressType = (addr) => {
  if (!addr) return 'Address';

  const isBilling = addr.default_billing === 1;
  const isShipping = addr.default_shipping === 1;

  if (isBilling && isShipping) return 'Billing & Shipping Address';
  if (isBilling) return 'Billing Address';
  if (isShipping) return 'Shipping Address';

  return 'Other Address';
};

export const getAddressTypeIndicator = (addr) => {
  if (!addr) {
    return {
      isBilling: false,
      isShipping: false,
      isBoth: false,
    };
  }

  const isBilling = addr.default_billing === 1;
  const isShipping = addr.default_shipping === 1;

  const color = isBilling && isShipping ? 'orange-inverse' : isBilling ? 'green-inverse' : isShipping ? 'blue-inverse' : 'gray';

  return {
    isBilling,
    isShipping,
    isBoth: isBilling && isShipping,
    color,
  };
};

export const sortPriceLevels = (levels = []) => {
  if (!Array.isArray(levels)) return [];

  const sorted = _.sortBy(levels, (item) => {
    const match = item.price_level.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : Infinity;
  });

  const numbered = sorted.filter((item) => /^\d+/.test(item.price_level));
  const unnumbered = sorted.filter((item) => !/^\d+/.test(item.price_level));

  return [...numbered, ..._.sortBy(unnumbered, ['price_level'])];
};

export const getFullName = (user) => {
  if (!user) return '';
  if (user.first_name === undefined) return '';
  return `${user.first_name} ${user.last_name || ''}`.trim();
};

export const getDateRangeFormat = (dates) => {
  if (!dates) return { start_date: null, end_date: null };

  const start_date = dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
  const end_date = dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;

  return { start_date, end_date };
};

export const dateRangeWithTime = (dates) => {
  if (!dates) return { start_date: null, end_date: null };

  const start_date = dates[0]
    ? dayjs(dates[0])
      .startOf('day')
      .subtract(5, 'hour')
      .format('YYYY-MM-DD HH:mm:ss')
    : null;

  const end_date = dates[1]
    ? dayjs(dates[1])
      .endOf('day')
      .subtract(5, 'hour')
      .format('YYYY-MM-DD HH:mm:ss')
    : null;

  return { start_date, end_date };
};

export const getTierOptions = (record) => {
  if (!record) return [];

  const tiers = record.tiers?.map(t => ({
    label: t.price_level,
    value: t.price_level,
    price: t.tier_price,
  })) || [];

  if (record.price_level && !tiers.find(t => t.value === record.price_level)) {
    tiers.unshift({
      label: record.price_level,
      value: record.price_level,
      price: record.unit_price || 0,
    });
  }

  return _.uniqBy(tiers, 'value');
};

export const getProductRibbon = (product) => {
  if (product?.promo_price && product?.promo_price <= product?.unit_price) {
    return { text: "Offer", color: "red" };
  }

  if (!product?.availability) {
    return { text: "Not Available", color: "volcano" };
  }

  return null;
};

export const formatCustomerDays = (data = {}) => {
  const getDays = (prefix) =>
    deliveryDays.filter((d) => {
      const key = `${prefix}_${d.label.toLowerCase()}`;
      return data[key] === 1;
    });

  return {
    deliveryDays: getDays("delivery"),
    orderDays: getDays("order"),
  };
};

export const getNetsuiteLink = ({ invoiceId, type }) => {
  if (!invoiceId) return null;
  return `${netsuite_url}/app/accounting/transactions/${type}.nl?id=${invoiceId}`;
};